import * as React from "react";
import { merge, $ } from "glamor";
import { ICellEditorParams } from "ag-grid-community/dist/lib/interfaces/iCellEditor";
import Editor from "draft-js-plugins-editor";
import { EditorState, SelectionState, getDefaultKeyBinding, KeyBindingUtil, Modifier, ContentState } from "draft-js";
import createMentionPlugin, { defaultSuggestionsFilter, getStyleMergeMixin } from "components/draft-js-mention";
import { Map, Set, List } from "immutable";
import classNames from "classnames";
import { Kinds } from "constants/product-collection";
import { KEYBOARD_KEYS } from "constants/keyboard";
import { DATATEST } from "constants/integration";

enum EDIT_MODES {
    REPLACE,
    APPEND,
}

// const hashtagPlugin = createHashtagPlugin();
const mentionPlugin = createMentionPlugin({
    mentionPrefix: "#",
    mentionTrigger: "#",
});
const { MentionSuggestions } = mentionPlugin;
export const plugins = [mentionPlugin];

const Entry = (props) => {
    const {
        mention,
        theme,
        searchValue, // tslint:disable-line no-unused-vars
        isFocused, // tslint:disable-line no-unused-vars
        // tslint:disable-next-line:trailing-comma
        ...parentProps // prettier-ignore
    } = props;

    return (
        <div {...parentProps}>
            <span className={theme.mentionSuggestionsEntryText}>{mention}</span>
        </div>
    );
};

interface IBlobWithHashTagsEditorParams extends ICellEditorParams {
    getHashtags: () => Set<string>;
    getSchema: () => List<any>;
}

interface IState {
    editorState: any;
    suggestions?: any;
    editMode: EDIT_MODES;
    focus: boolean;
}

export class TextEditor extends React.PureComponent<IBlobWithHashTagsEditorParams, IState> {
    state = {
        editorState: undefined,
        suggestions: [],
        editMode: EDIT_MODES.APPEND,
        focus: false,
    };

    private editor;
    private unmounted = false;

    private styles = merge(
        $(".inline .DraftEditor-root", {
            height: "64px",
            width: "280px",
        }),
        $(" .DraftEditor-root", {
            height: "128px",
            width: "320px",
            overflowY: "scroll",
        }),
        $(" .DraftEditor-editorContainer, .public-DraftEditor-content", {
            height: "100%",
            width: "100%",
        }),
        $(" .public-DraftEditor-content", {
            padding: "4px 8px",
        }),
        ...getStyleMergeMixin()
    );

    private isMultiline = false;
    private takeDefaultValue = false;
    private defaultValue = "";
    private isMobileiOS;

    constructor(props) {
        super(props);

        let editMode = EDIT_MODES.APPEND;
        let value = props.value || "";
        this.defaultValue = value;
        if (props.eventKey === KEYBOARD_KEYS.BACKSPACE || props.eventKey === KEYBOARD_KEYS.DELETE) {
            value = "";
        } else if (props.charPress) {
            value = props.charPress;
            editMode = EDIT_MODES.REPLACE;
        }

        this.state = {
            editorState: EditorState.createWithContent(ContentState.createFromText(value)),
            suggestions: props.getHashtags?.().toJS() || [],
            editMode,
            focus: false,
        };

        const column = this.getColumnFromSchema(props);
        if (column) {
            const kind = column.get("kind");
            if (kind === Kinds.MULTILINE_TEXT) {
                this.isMultiline = true;
            }
        }
        this.isMobileiOS = /iPad|iPhone/.test(window.navigator.userAgent);
    }

    componentDidMount() {
        this.focus();
    }

    componentWillUnmount() {
        this.unmounted = true;
    }

    getColumnFromSchema(props = this.props): Map<string, any> | undefined {
        const colId = (this.props.column as any).colId;
        const [, column] = this.props.getSchema().findEntry((schemaEntry) => schemaEntry.get("field_name") === colId);
        return column;
    }

    focus() {
        if (!this.unmounted && this.editor) {
            let editorState = this.state.editorState;
            if (this.selectAllOnStart()) {
                const currentContent = editorState.getCurrentContent();
                const firstBlock = currentContent.getBlockMap().first();
                const lastBlock = currentContent.getBlockMap().last();
                const firstBlockKey = firstBlock.getKey();
                const lastBlockKey = lastBlock.getKey();
                const lengthOfLastBlock = lastBlock.getLength();

                const selection = new SelectionState({
                    anchorKey: firstBlockKey,
                    anchorOffset: 0,
                    focusKey: lastBlockKey,
                    focusOffset: lengthOfLastBlock,
                });
                editorState = EditorState.forceSelection(editorState, selection);
            } else {
                editorState = EditorState.moveFocusToEnd(editorState);
            }

            if (this.props.eventKey === KEYBOARD_KEYS.ENTER) {
                // Wrapping inside nextTick to let other ENTER event listener dispatches first
                process.nextTick(() => {
                    this.setState({
                        editorState,
                    });
                });
            } else {
                this.setState({
                    editorState,
                });
            }
        }
    }

    getValue() {
        return this.takeDefaultValue ? this.defaultValue : this.state.editorState.getCurrentContent().getPlainText();
    }

    isPopup = (): boolean => {
        return true;
    };

    // Check if cell should select all when starts editing
    selectAllOnStart() {
        const { charPress, eventKey } = this.props;
        if (eventKey === KEYBOARD_KEYS.ENTER) {
            // On ENTER press
            return false;
        } else if (!eventKey && !charPress) {
            // On mouse double click or focused on tab
            return true;
        }

        return false;
    }

    handlePaste = (text: string | null) => {
        if (!text) {
            // We could also receive null
            text = "";
        }
        const { editorState } = this.state;

        if (!this.isMultiline) {
            this.handleChange(
                EditorState.push(
                    editorState,
                    Modifier.replaceText(
                        editorState.getCurrentContent(),
                        editorState.getSelection(),
                        text.replace(/\n/g, " ")
                    ),
                    "remove-range"
                )
            );
            return "handled";
        } else {
            return "not-handled";
        }
    };

    render() {
        const { editorState, focus } = this.state;
        return (
            <div {...this.styles} className={classNames({ inline: !this.isMultiline })} onKeyDown={this.handleKeyDown}>
                <Editor
                    tabIndex={0}
                    webDriverTestID={DATATEST.ASSORTMENTS.GRID.TEXT_CELL_EDITOR}
                    editorState={editorState}
                    onChange={this.handleChange}
                    plugins={plugins}
                    ref={this.bindEditor}
                    handleKeyCommand={this.handleKeyCommand}
                    keyBindingFn={this.handleKeyBinding}
                    onBlur={this.handleBlur}
                    onFocus={this.handleFocus}
                    handlePastedText={this.handlePaste}
                />
                <MentionSuggestions
                    onSearchChange={this.handleSearchChange}
                    suggestions={this.state.suggestions}
                    entryComponent={Entry}
                    focus={focus}
                />
            </div>
        );
    }

    private bindEditor = (ref) => (this.editor = ref);

    private handleSearchChange = ({ value }) => {
        const suggestions = defaultSuggestionsFilter(value, this.props.getHashtags?.().toJS() || []);
        if (!this.unmounted) {
            this.setState({
                suggestions,
            });
        }
        return suggestions;
    };

    private handleChange = (editorState) => {
        if (!this.unmounted) {
            this.setState({
                editorState,
            });
        }
    };

    private handleBlur = (e) => {
        if (!this.unmounted) {
            this.setState({ focus: false });
        }
        if (this.isMobileiOS) {
            this.props.stopEditing();
        }
    };

    private handleFocus = () => {
        this.setState({ focus: true });
    };

    private handleKeyCommand = (command: string) => {
        if (command === "stopEditing") {
            this.props.stopEditing();
            this.props.api.clearRangeSelection();
            return "handled";
        }
        return "not-handled";
    };

    private handleKeyBinding = (e) => {
        const { editMode } = this.state;
        const isCtrlKey = KeyBindingUtil.isCtrlKeyCommand(e);
        const isOptionKey = KeyBindingUtil.isOptionKeyCommand(e);
        const isCommandKey = KeyBindingUtil.hasCommandModifier(e);
        switch (e.key) {
            case KEYBOARD_KEYS.ENTER:
                if (this.isMultiline) {
                    if (!isCtrlKey && !isOptionKey && !isCommandKey) {
                        return "stopEditing";
                    }
                } else {
                    return "stopEditing";
                }
                break;
            default:
        }
        return getDefaultKeyBinding(e);
    };

    private handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const { editMode } = this.state;
        switch (e.key) {
            case KEYBOARD_KEYS.LEFT:
            case KEYBOARD_KEYS.RIGHT:
            case KEYBOARD_KEYS.UP:
            case KEYBOARD_KEYS.DOWN:
                // Suggestions will cause defaultPrevented to be true if using key events
                if (!e.defaultPrevented && editMode === EDIT_MODES.REPLACE) {
                    this.props.stopEditing();
                    this.navigateToCell(e);
                    e.preventDefault();
                }
                break;
            default:
        }
    };

    private navigateToCell = (e: React.KeyboardEvent<HTMLDivElement>) => {
        const { api } = this.props;
        const focusedCell = api.getFocusedCell();
        api.clearRangeSelection();
        switch (e.key) {
            case KEYBOARD_KEYS.RIGHT:
                api.tabToNextCell();
                break;
            case KEYBOARD_KEYS.LEFT:
                api.tabToPreviousCell();
                break;
            case KEYBOARD_KEYS.UP:
                if (focusedCell.rowIndex > 0) {
                    api.setFocusedCell(focusedCell.rowIndex - 1, focusedCell.column.getColId());
                }
                break;
            case KEYBOARD_KEYS.DOWN:
                api.setFocusedCell(focusedCell.rowIndex + 1, focusedCell.column.getColId(), null);
                break;
            case KEYBOARD_KEYS.ENTER:
                // covering "new line"
                if (e.ctrlKey) {
                    api.setFocusedCell(focusedCell.rowIndex + 1, focusedCell.column.getColId(), null);
                }
                break;
            default:
        }
    };
}
